import React, { useMemo } from 'react';
import { diffWords } from 'diff';
import { ArrowRight, X, Check, Copy, CheckCircle2, Trash2 } from 'lucide-react';
import { detectLanguage, highlightLine } from '../utils/syntaxHighlight';
import { DiffChange, MergeKeep } from '../utils/diffEngine';

interface DiffVisualizerProps {
  original: string;
  modified: string;
  /** Output of computeLineDiff(); shared with App so merge indices line up. */
  changes: DiffChange[];
  onMerge: (index: number, keep: MergeKeep) => void;
  isMerging?: boolean;
  viewMode?: 'split' | 'unified';
  mergedChunkIndices?: Set<number>;
  theme: 'light' | 'dark';
  activeDiffIndex?: number;
}

interface SplitBlockRow {
  originalLineNumber?: number;
  modifiedLineNumber?: number;
  originalContent: string;
  modifiedContent: string;
}

interface SplitBlock {
  type: 'unchanged' | 'added' | 'removed' | 'modified';
  ignored?: boolean;
  leftChange?: DiffChange;
  leftIndex?: number;
  rightChange?: DiffChange;
  rightIndex?: number;
  rows: SplitBlockRow[];
}

const toLines = (value: string): string[] => {
  const lines = value.split('\n');
  if (value.endsWith('\n')) lines.pop();
  return lines.length === 0 ? [''] : lines;
};

export const DiffVisualizer: React.FC<DiffVisualizerProps> = ({ 
  original,
  modified,
  changes,
  onMerge,
  isMerging,
  viewMode = 'split',
  mergedChunkIndices = new Set(),
  theme,
  activeDiffIndex = 0
}) => {
  const isDark = theme === 'dark';

  const colors = {
    removed: {
      bg: isDark ? 'bg-red-950/40' : 'bg-red-50',
      text: isDark ? 'text-red-200' : 'text-red-900',
      border: 'border-red-500/50',
      gutter: isDark ? 'bg-red-950/60 text-red-400/70 border-red-900/30' : 'bg-red-100 text-red-400 border-red-200',
      word: isDark ? 'bg-red-500/30 text-red-100 font-bold' : 'bg-red-200 text-red-900 font-bold',
    },
    added: {
      bg: isDark ? 'bg-emerald-950/40' : 'bg-emerald-50',
      text: isDark ? 'text-emerald-200' : 'text-emerald-900',
      border: 'border-emerald-500/50',
      gutter: isDark ? 'bg-emerald-950/60 text-emerald-400/70 border-emerald-900/30' : 'bg-emerald-100 text-emerald-400 border-emerald-200',
      word: isDark ? 'bg-emerald-500/30 text-emerald-100 font-bold' : 'bg-emerald-200 text-emerald-900 font-bold',
    },
    modified: {
      bg: isDark ? 'bg-amber-900/10' : 'bg-amber-50/50',
      border: 'border-amber-500/30',
      wordHighlight: isDark ? 'bg-amber-500/30 text-amber-100' : 'bg-amber-200 text-amber-950 font-bold',
    },
    unchanged: {
      text: isDark ? 'text-slate-400' : 'text-slate-700',
      gutter: isDark ? 'bg-slate-900/40 text-slate-600 border-slate-800' : 'bg-slate-50 text-slate-400 border-slate-100',
    }
  };

  const language = useMemo(() => {
    return detectLanguage(original || modified);
  }, [original, modified]);

  const splitBlocks = useMemo(() => {
    const blocks: SplitBlock[] = [];
    let originalLine = 1;
    let modifiedLine = 1;
    let i = 0;

    while (i < changes.length) {
      const current = changes[i];
      const next = changes[i + 1];

      const currentLines = toLines(current.value);

      if (current.removed && next && next.added) {
        const nextLines = toLines(next.value);

        const count = Math.max(currentLines.length, nextLines.length);
        const rows: SplitBlockRow[] = [];

        for (let j = 0; j < count; j++) {
          rows.push({
            originalLineNumber: j < currentLines.length ? originalLine + j : undefined,
            modifiedLineNumber: j < nextLines.length ? modifiedLine + j : undefined,
            originalContent: currentLines[j] || '',
            modifiedContent: nextLines[j] || '',
          });
        }
        
        blocks.push({
          type: 'modified',
          ignored: current.ignored,
          leftChange: current,
          leftIndex: i,
          rightChange: next,
          rightIndex: i + 1,
          rows
        });

        originalLine += currentLines.length;
        modifiedLine += nextLines.length;
        i += 2;
      } else if (current.removed) {
        const rows: SplitBlockRow[] = currentLines.map((line, idx) => ({
          originalLineNumber: originalLine + idx,
          originalContent: line,
          modifiedContent: '',
        }));
        blocks.push({ type: 'removed', ignored: current.ignored, leftChange: current, leftIndex: i, rows });
        originalLine += currentLines.length;
        i++;
      } else if (current.added) {
        const rows: SplitBlockRow[] = currentLines.map((line, idx) => ({
          modifiedLineNumber: modifiedLine + idx,
          modifiedContent: line,
          originalContent: '',
        }));
        blocks.push({ type: 'added', ignored: current.ignored, rightChange: current, rightIndex: i, rows });
        modifiedLine += currentLines.length;
        i++;
      } else {
        // leftValue carries the original text when lines only match via ignore options
        const leftLines = current.leftValue !== undefined ? toLines(current.leftValue) : currentLines;
        const rows: SplitBlockRow[] = currentLines.map((line, idx) => ({
          originalLineNumber: originalLine + idx,
          modifiedLineNumber: modifiedLine + idx,
          originalContent: leftLines[idx] ?? line,
          modifiedContent: line,
        }));
        blocks.push({ type: 'unchanged', leftChange: current, leftIndex: i, rows });
        originalLine += currentLines.length;
        modifiedLine += currentLines.length;
        i++;
      }
    }
    return blocks;
  }, [changes]);

  const renderWordDiff = (orig: string, mod: string, type: 'original' | 'modified') => {
    if (!orig && type === 'original') return null;
    if (!mod && type === 'modified') return null;
    
    if (orig && mod) {
      const wordChanges = diffWords(orig, mod);
      return (
        <span>
          {wordChanges.map((part, index) => {
            const addedClass = colors.added.word;
            const removedClass = colors.removed.word;
            if (type === 'original') {
              if (part.added) return null;
              if (part.removed) return <span key={index} className={`${removedClass} rounded-[2px] px-[1px]`}>{part.value}</span>;
              return <span key={index}>{part.value}</span>;
            }
            if (type === 'modified') {
              if (part.removed) return null;
              if (part.added) return <span key={index} className={`${addedClass} rounded-[2px] px-[1px]`}>{part.value}</span>;
              return <span key={index}>{part.value}</span>;
            }
            return null;
          })}
        </span>
      );
    }
    return <span>{type === 'original' ? orig : mod}</span>;
  };

  const ActionButton = ({ onClick, color, icon: Icon, label, title }: {
    onClick: () => void;
    color: 'slate' | 'emerald' | 'rose';
    icon: React.ComponentType<{ size?: number; 'aria-hidden'?: boolean }>;
    label: string;
    title: string;
  }) => {
    const colorClasses: Record<string, string> = {
      slate: isDark ? 'bg-slate-800 hover:bg-slate-700 hover:border-slate-500 text-slate-100 border-slate-700' : 'bg-slate-100 hover:bg-slate-200 hover:border-slate-400 text-slate-700 border-slate-300',
      emerald: 'bg-emerald-600 hover:bg-emerald-500 hover:brightness-110 text-white border-emerald-800',
      rose: 'bg-rose-700 hover:bg-rose-600 hover:brightness-110 text-white border-rose-800',
    };

    return (
      <button 
        onClick={onClick}
        className={`flex items-center gap-2 px-3 py-2 text-[10px] sm:text-[11px] font-bold rounded-lg border shadow-lg transition-all hover:scale-110 active:scale-95 ${colorClasses[color]}`}
        title={title}
        aria-label={title}
      >
        <Icon size={12} aria-hidden /> <span className="hidden xs:inline">{label}</span>
      </button>
    );
  };

  const MergedIndicator = () => (
    <div className={`flex items-center gap-2 px-3 py-1.5 text-[10px] font-bold rounded-lg border transition-all hover:scale-105 cursor-default ${isDark ? 'border-emerald-800/50 bg-emerald-900/20 text-emerald-400' : 'border-emerald-200 bg-emerald-50 text-emerald-700'}`}>
      <CheckCircle2 size={12} />
      <span>Merged</span>
    </div>
  );

  const renderActions = (block: SplitBlock) => {
    const { type, leftChange, leftIndex, rightIndex } = block;
    const isMerged = (leftIndex !== undefined && mergedChunkIndices.has(leftIndex)) || 
                     (rightIndex !== undefined && mergedChunkIndices.has(rightIndex));
    if (isMerged) return <MergedIndicator />;

    return (
      <div className={`flex flex-col sm:flex-row gap-1.5 p-1.5 border rounded-xl shadow-2xl transition-all ${isDark ? 'bg-slate-900 border-slate-700 shadow-slate-950/50' : 'bg-white border-slate-200 shadow-slate-200/50'}`}>
        {type === 'modified' && leftIndex !== undefined && rightIndex !== undefined ? (
          <>
            <ActionButton onClick={() => onMerge(leftIndex, 'original')} color="slate" icon={ArrowRight} label="Keep Original" title="Keep the original version of this text" />
            <ActionButton onClick={() => onMerge(rightIndex, 'modified')} color="emerald" icon={Check} label="Accept Modified" title="Use the modified version of this text" />
          </>
        ) : type === 'removed' && leftChange && leftIndex !== undefined ? (
          <>
            <ActionButton onClick={() => onMerge(leftIndex, 'original')} color="slate" icon={ArrowRight} label="Keep" title="Keep this block of original text" />
            <ActionButton onClick={() => onMerge(leftIndex, 'modified')} color="rose" icon={Trash2} label="Remove" title="Accept the removal of this block" />
            <ActionButton onClick={() => navigator.clipboard.writeText(leftChange.value)} color="slate" icon={Copy} label="Copy" title="Copy content to clipboard" />
          </>
        ) : type === 'added' && rightIndex !== undefined ? (
          <>
            <ActionButton onClick={() => onMerge(rightIndex, 'modified')} color="emerald" icon={Check} label="Accept" title="Accept and add this new block of text" />
            <ActionButton onClick={() => onMerge(rightIndex, 'original')} color="rose" icon={X} label="Reject" title="Ignore this addition" />
          </>
        ) : null}
      </div>
    );
  };

  const EmptyState = () => (
    <div className="p-10 sm:p-20 text-center text-slate-500 flex flex-col items-center gap-4 animate-in fade-in zoom-in duration-500">
      <div className={`p-4 rounded-2xl border transition-all hover:rotate-6 hover:scale-110 ${isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200'}`}>
        <CheckCircle2 size={40} className="text-emerald-500 opacity-50" />
      </div>
      <div className="flex flex-col gap-1">
        <span className={`text-lg sm:text-xl font-bold ${isDark ? 'text-slate-400' : 'text-slate-800'}`}>Workspace Identical</span>
        <span className="text-xs sm:text-sm">No differences found between original and modified source.</span>
      </div>
    </div>
  );

  const hasChanges = changes.some(c => (c.added || c.removed) && !c.ignored);
  if (!hasChanges) return <EmptyState />;

  if (viewMode === 'unified') {
    return (
      <div className={`flex flex-col w-full h-full overflow-auto rounded-xl shadow-inner border font-mono text-[11px] sm:text-xs relative transition-all ${isDark ? 'bg-slate-950 border-slate-800' : 'bg-white border-slate-200'}`}>
        <div className={`flex border-b text-[9px] sm:text-[10px] font-black uppercase tracking-widest py-2 sticky top-0 z-40 ${isDark ? 'bg-slate-900 border-slate-800 text-slate-500' : 'bg-slate-100 border-slate-200 text-slate-600'}`}>
          <div className="w-8 sm:w-12 text-right px-1 sm:px-2">Old</div>
          <div className="w-8 sm:w-12 text-right px-1 sm:px-2 border-r border-slate-200/20">New</div>
          <div className="px-3 sm:px-6">Content Transformation</div>
        </div>
        {splitBlocks.map((block, index) => {
          let colorClass = colors.unchanged.text;
          let borderClass = 'border-transparent';
          let gutterClass = colors.unchanged.gutter;

          if (block.ignored) {
            colorClass = `${colors.unchanged.text} opacity-60`;
          } else if (block.type === 'added') {
            colorClass = `${colors.added.bg} ${colors.added.text}`;
            borderClass = colors.added.border;
            gutterClass = colors.added.gutter;
          } else if (block.type === 'removed') {
            colorClass = `${colors.removed.bg} ${colors.removed.text}`;
            borderClass = colors.removed.border;
            gutterClass = colors.removed.gutter;
          } else if (block.type === 'modified') {
            colorClass = colors.modified.bg;
            borderClass = colors.modified.border;
          }

          return (
            <div 
              key={index} 
              id={`diff-chunk-${block.leftIndex ?? block.rightIndex}`}
              className={`group relative border-l-2 sm:border-l-4 transition-all scroll-mt-24 ${colorClass} ${borderClass}`}
            >
              {(block.type !== 'unchanged') && !block.ignored && !isMerging && (
                <div className="absolute right-4 top-2 z-20 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-all transform translate-x-4 group-hover:translate-x-0 group-focus-within:translate-x-0">
                   {renderActions(block)}
                </div>
              )}
              {block.rows.map((row, lIdx) => (
                <div key={lIdx} className={`flex items-stretch hover:bg-blue-500/10 transition-colors`}>
                  <div className={`flex-none flex text-[9px] sm:text-[10px] font-mono select-none border-r h-6 ${gutterClass}`}>
                    <div className="w-8 sm:w-12 text-right px-1 sm:px-2 leading-6">{row.originalLineNumber || ''}</div>
                    <div className="w-8 sm:w-12 text-right px-1 sm:px-2 leading-6 border-l border-slate-200/20">{row.modifiedLineNumber || ''}</div>
                  </div>
                  <div className="w-5 sm:w-6 flex-none select-none text-center leading-6 opacity-50 font-bold">{block.type === 'added' ? '+' : block.type === 'removed' ? '-' : block.type === 'modified' ? 'M' : ''}</div>
                  <pre className="flex-1 px-2 sm:px-4 leading-6 whitespace-pre-wrap break-all font-mono">
                    {block.type === 'modified' ? renderWordDiff(row.originalContent, row.modifiedContent, 'modified') :
                     <span dangerouslySetInnerHTML={{ __html: highlightLine(row.modifiedContent || row.originalContent, language) }} />}
                  </pre>
                </div>
              ))}
            </div>
          );
        })}
      </div>
    );
  }

  // Split View (Default)
  return (
    <div className={`flex flex-col w-full h-full overflow-auto rounded-xl shadow-inner border font-mono text-[11px] sm:text-xs relative transition-all ${isDark ? 'bg-slate-950 border-slate-800' : 'bg-white border-slate-200'}`}>
       <div className={`flex border-b text-[9px] sm:text-[10px] font-black uppercase tracking-widest py-2 sticky top-0 z-40 min-w-[600px] sm:min-w-0 ${isDark ? 'bg-slate-900 border-slate-800 text-slate-500' : 'bg-slate-100 border-slate-200 text-slate-600'}`}>
          <div className="w-1/2 flex border-r border-slate-200/20">
             <div className="w-10 sm:w-14 text-right px-2 sm:px-3 border-r border-slate-200/20">Original</div>
             <div className="px-3 sm:px-6">Content</div>
          </div>
          <div className="w-1/2 flex">
             <div className="w-10 sm:w-14 text-right px-2 sm:px-3 border-r border-slate-200/20">Modified</div>
             <div className="px-3 sm:px-6">Content</div>
          </div>
       </div>
       {splitBlocks.map((block, idx) => {
          if (block.ignored) {
            block = { ...block, type: 'unchanged' };
          }
          const lCol = block.type === 'removed' ? `${colors.removed.bg} ${colors.removed.text}` :
                       block.type === 'modified' ? colors.modified.bg : '';
          const lGutter = block.type === 'removed' ? colors.removed.gutter : colors.unchanged.gutter;

          const rCol = block.type === 'added' ? `${colors.added.bg} ${colors.added.text}` :
                       block.type === 'modified' ? colors.modified.bg : '';
          const rGutter = block.type === 'added' ? colors.added.gutter : colors.unchanged.gutter;

          return (
            <div 
              key={idx} 
              id={`diff-chunk-${block.leftIndex ?? block.rightIndex}`}
              className="group relative w-full border-b border-slate-200/10 min-w-[600px] sm:min-w-0 transition-all scroll-mt-24"
            >
              {(block.type !== 'unchanged') && (
                <div className="absolute top-2 left-1/2 -translate-x-1/2 z-30 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-all transform -translate-y-2 group-hover:translate-y-0 group-focus-within:translate-y-0 sm:scale-100 scale-90">
                   {renderActions(block)}
                </div>
              )}
              {block.rows.map((row, rIdx) => (
                <div key={rIdx} className="flex hover:bg-blue-500/10 transition-colors">
                   <div className={`w-1/2 flex border-r relative ${lCol} transition-colors ${isDark ? 'border-slate-800' : 'border-slate-100'}`}>
                      <div className={`w-10 sm:w-14 flex-none text-right px-2 sm:px-3 text-[9px] sm:text-[10px] leading-6 border-r h-6 select-none ${lGutter}`}>{row.originalLineNumber}</div>
                      <pre className="flex-1 px-3 sm:px-4 leading-6 whitespace-pre-wrap break-all font-mono">
                        {block.type === 'modified' ? renderWordDiff(row.originalContent, row.modifiedContent, 'original') :
                         <span dangerouslySetInnerHTML={{ __html: highlightLine(row.originalContent, language) }} />}
                      </pre>
                   </div>
                   <div className={`w-1/2 flex relative ${rCol} transition-colors`}>
                      <div className={`w-10 sm:w-14 flex-none text-right px-2 sm:px-3 text-[9px] sm:text-[10px] leading-6 border-r h-6 select-none ${rGutter}`}>{row.modifiedLineNumber}</div>
                      <pre className="flex-1 px-3 sm:px-4 leading-6 whitespace-pre-wrap break-all font-mono">
                        {block.type === 'modified' ? renderWordDiff(row.originalContent, row.modifiedContent, 'modified') :
                         <span dangerouslySetInnerHTML={{ __html: highlightLine(row.modifiedContent, language) }} />}
                      </pre>
                   </div>
                </div>
              ))}
            </div>
          );
       })}
    </div>
  );
};